import { useEffect, useState } from "react";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { PlayerProfileOutput } from "types/web-app";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { ProfileTeamDisplay } from "@/components/profile/profile-team-display";
import { PokemonPreviewBox } from "@/components/profile/pokemon-preview-box";
import { DuelHistoryCard } from "@/components/profile/duel-history-card";
import { RaidHistoryCard } from "@/components/profile/raid-history-card";
import { Pokemon } from "prisma-layer";

export const PlayerProfileView = () => {
  const { getAccessToken } = useAuth();
  const { viewedPlayerId, setCurrentRoute } = useSPAContext();
  const [profile, setProfile] = useState<PlayerProfileOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"team" | "duel" | "raid">("team");
  const [previewPokemon, setPreviewPokemon] = useState<Pick<
    Pokemon,
    | "id"
    | "display_id"
    | "base_pokemon_name"
    | "level"
    | "talents"
    | "active_skills"
    | "is_shiny"
  > | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = getAccessToken();
      if (!token) return;

      try {
        const res = await getPlayerProfile(token, viewedPlayerId);
        if (res.ok && res.data) {
          setProfile(res.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [getAccessToken, viewedPlayerId]);

  if (loading)
    return (
      <div className="text-center p-4 text-pk-gray-4">Loading profile...</div>
    );
  if (!profile)
    return (
      <div className="text-center p-4 text-pk-gray-4">Profile not found</div>
    );

  return (
    <div className="flex flex-col gap-1 xs:gap-2 pb-10 xs:pb-20 p-2 w-full max-w-2xl mx-auto bg-black/60 h-full ">
      {/* Header Card */}
      <StandardFrame variant="night" className=" flex flex-col gap-2 xs:gap-4 ">
        <div className="p-1 flex items-center justify-between bg-pk-night-2">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl xs:text-2xl font-bold text-pk-gray-5">
                {profile.username}
              </h1>
              <div className="flex items-center gap-2">
                <CountryFlag countryCode={profile.country} size="sm" />
                <span className="text-pk-gray-4 font-bold text-xs xs:text-sm">
                  {profile.country || "Unknown"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-4 gap-2 border-t border-pk-gray-2 pt-2 xs:pt-4">
          <div className="flex flex-col items-center">
            <span className="text-xs text-pk-gray-4 font-bold uppercase">
              ELO
            </span>
            <span className="text-lg xs:text-xl font-bold text-pk-blue-5">
              {profile.stats.elo}
            </span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs text-pk-gray-4 font-bold uppercase">
              Wins
            </span>
            <span className="text-lg xs:text-xl font-bold text-green-600">
              {profile.stats.wins}
            </span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs text-pk-gray-4 font-bold uppercase">
              Losses
            </span>
            <span className="text-lg xs:text-xl font-bold text-red-500">
              {profile.stats.losses}
            </span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs text-pk-gray-4 font-bold uppercase">
              Total
            </span>
            <span className="text-lg xs:text-xl font-bold text-pk-gray-5">
              {profile.stats.duels}
            </span>
          </div>
        </div>
      </StandardFrame>

      <StandardFrame variant="night" className="flex flex-col gap-0">
        <div className="flex rounded-lg bg-pk-night-2 p-1 gap-1">
          {[
            { key: "team", label: "Main Team" },
            { key: "duel", label: "Duels" },
            { key: "raid", label: "Raids" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as "team" | "duel" | "raid")}
              className={`flex-1 rounded-md px-1.5 xs:px-3 py-1 xs:py-2 text-xs xs:text-sm transition border ${
                activeTab === tab.key
                  ? "bg-pk-blue-3 text-pk-gray-5 border-pk-blue-4"
                  : "bg-pk-night-3 text-pk-gray-4 border-pk-gray-2 hover:text-pk-gray-5"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "team" && (
          <div className="flex flex-col gap-2">
            <h2 className="text-pk-gray-4 font-bold uppercase text-sm pl-2">
              Main Team
            </h2>
            {profile.main_team.length > 0 ? (
              <div className="flex flex-col gap-4 overflow-y-auto min-h-0">
                <div className="bg-black/20 backdrop-blur-md rounded-xl border border-white/10 overflow-y-visible overflow-x-hidden flex-shrink-0">
                  <ProfileTeamDisplay
                    pokemons={profile.main_team}
                    onPokemonHover={(p) => setPreviewPokemon(p)}
                    activePokemonId={previewPokemon?.id ?? null}
                  />
                </div>
                {previewPokemon && (
                  <div className="px-1 flex-shrink-0">
                    <PokemonPreviewBox pokemon={previewPokemon} />
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-pk-gray-4 italic bg-black/5 rounded-lg">
                No Pokémon in main team
              </div>
            )}
          </div>
        )}

        {activeTab === "duel" && (
          <div className="flex flex-col gap-2">
            <h2 className="text-pk-gray-4 font-bold uppercase text-sm pl-2">
              Duel History
            </h2>
            {profile.match_history.length === 0 ? (
              <div className="p-8 text-center text-pk-gray-4 italic bg-black/5 rounded-lg">
                No recent duels
              </div>
            ) : (
              <div className="flex flex-col gap-2 overflow-y-auto max-h-[300px] pr-2 scrollbar-thin scrollbar-thumb-pk-gray-3 scrollbar-track-transparent">
                {profile.match_history.map((match) => (
                  <DuelHistoryCard key={match.duel_id} match={match} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "raid" && (
          <div className="flex flex-col gap-2">
            <h2 className="text-pk-gray-4 font-bold uppercase text-sm pl-2">
              Raid History
            </h2>
            {profile.raid_history.length === 0 ? (
              <div className="p-8 text-center text-pk-gray-4 italic bg-black/5 rounded-lg">
                No recent raids
              </div>
            ) : (
              <div className="flex flex-col gap-2 overflow-y-auto max-h-[300px] pr-2 scrollbar-thin scrollbar-thumb-pk-gray-3 scrollbar-track-transparent">
                {profile.raid_history.map((raid) => (
                  <RaidHistoryCard key={raid.raid_id} raid={raid} />
                ))}
              </div>
            )}
          </div>
        )}
      </StandardFrame>
    </div>
  );
};
